import { useState, useRef, useEffect, RefCallback } from 'react';

interface UseIntersectionObserverReturn {
  setRef: (id: string) => RefCallback<HTMLElement>;
  isVisible: (id: string) => boolean;
}

export function useIntersectionObserver(
  options: IntersectionObserverInit = {}
): UseIntersectionObserverReturn {
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, ...options }
    );

    const refs = sectionRefs.current;
    Object.values(refs).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const setRef = (id: string): RefCallback<HTMLElement> =>
    (el: HTMLElement | null) => {
      sectionRefs.current[id] = el;
    };

  const isVisible = (id: string): boolean => visibleSections.has(id);

  return { setRef, isVisible };
}

export function useScrolled(threshold = 60): boolean {
  const [scrolled, setScrolled] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = (): void => setScrolled(window.scrollY > threshold);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold]);

  return scrolled;
}
